import React, { useState, useEffect } from 'react';

declare global {
  interface Window { grecaptcha: any; }
}

interface AuthModalProps {
  show: boolean;
  onClose: () => void;
  onLogin: (token: string, user: { username: string; onboarding_complete: boolean }) => void;
}

export default function AuthModal({ show, onClose, onLogin }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register' | 'verify-pending' | 'forgot' | 'reset-sent'>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteKey, setSiteKey] = useState('');

  useEffect(() => {
    if (show && !siteKey) {
      fetch('/api/config').then(r => r.json()).then(data => {
        const key = data.recaptcha_site_key;
        if (key) {
          setSiteKey(key);
          if (!document.querySelector(`script[src*="recaptcha"]`)) {
            const s = document.createElement('script');
            s.src = `https://www.google.com/recaptcha/api.js?render=${key}`;
            s.async = true;
            document.head.appendChild(s);
          }
        }
      }).catch(() => {});
    }
  }, [show, siteKey]);

  if (!show) return null;

  const reset = () => {
    setUsername(''); setEmail(''); setPassword(''); setConfirmPassword(''); setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Login failed'); return; }
      onLogin(data.access_token, { username: data.username, onboarding_complete: data.onboarding_complete });
      reset(); onClose();
    } catch { setError('Network error'); } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Failed'); return; }
      setMode('reset-sent');
    } catch { setError('Network error'); } finally { setLoading(false); }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      let recaptcha_token = '';
      try {
        await new Promise<void>((resolve) => {
          if (window.grecaptcha?.ready) {
            window.grecaptcha.ready(() => resolve());
          } else {
            resolve();
          }
        });
        if (window.grecaptcha && siteKey) {
          recaptcha_token = await window.grecaptcha.execute(siteKey, { action: 'register' });
        }
      } catch (err) {
        console.warn('reCAPTCHA failed:', err);
      }

      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, recaptcha_token }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Registration failed'); return; }
      setMode('verify-pending');
    } catch { setError('Network error'); } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal auth-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #5f99cf', marginBottom: 10, paddingBottom: 5 }}>
          <div className="nav-tabs" style={{ gap: 0 }}>
            {mode !== 'verify-pending' && <>
              <button className={`nav-tab ${mode === 'login' ? 'active' : ''}`}
                onClick={() => { setMode('login'); setError(''); }}>login</button>
              <button className={`nav-tab ${mode === 'register' ? 'active' : ''}`}
                onClick={() => { setMode('register'); setError(''); }}>register</button>
            </>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>x</button>
        </div>

        {error && <div style={{ color: '#ff4500', fontSize: 11, marginBottom: 8 }}>{error}</div>}

        {mode === 'login' && (
          <form className="auth-form" onSubmit={handleLogin}>
            <label>username</label>
            <input value={username} onChange={e => setUsername(e.target.value)} required autoFocus />
            <label>password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            <button type="submit" disabled={loading}>{loading ? 'logging in...' : 'login'}</button>
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <button type="button" onClick={() => { setMode('forgot'); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#5f99cf', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' }}>
                forgot password?
              </button>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form className="auth-form" onSubmit={handleForgotPassword}>
            <p style={{ fontSize: 12, marginBottom: 10 }}>Enter the email address associated with your account.</p>
            <label>email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
            <button type="submit" disabled={loading}>{loading ? 'sending...' : 'send reset link'}</button>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={() => { setMode('login'); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#5f99cf', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' }}>
                back to login
              </button>
            </div>
          </form>
        )}

        {mode === 'reset-sent' && (
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <p style={{ fontSize: 13, marginBottom: 10 }}>If an account exists for <b>{email}</b>, a reset link has been sent.</p>
            <p style={{ fontSize: 11, color: '#888' }}>Check your email and click the link to set a new password. The link expires in 1 hour.</p>
            <button onClick={() => { setMode('login'); reset(); }}
              style={{ marginTop: 15, background: '#5f99cf', color: 'white', border: 'none', padding: '4px 12px', cursor: 'pointer' }}>
              back to login
            </button>
          </div>
        )}

        {mode === 'register' && (
          <form className="auth-form" onSubmit={handleRegister}>
            <label>username</label>
            <input value={username} onChange={e => setUsername(e.target.value)} required autoFocus />
            <label>email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            <label>password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            <label>confirm password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
            <button type="submit" disabled={loading}>{loading ? 'registering...' : 'register'}</button>
          </form>
        )}

        {mode === 'verify-pending' && (
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <p style={{ fontSize: 13, marginBottom: 10 }}>A verification link has been sent to <b>{email}</b>.</p>
            <p style={{ fontSize: 11, color: '#888' }}>Check your email (or the server console in dev mode) and click the link to activate your account.</p>
            <button onClick={() => { setMode('login'); reset(); }}
              style={{ marginTop: 15, background: '#5f99cf', color: 'white', border: 'none', padding: '4px 12px', cursor: 'pointer' }}>
              back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
